import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import {
  addWeeks,
  eachWeekOfInterval,
  endOfWeek,
  format,
  isAfter,
  max,
  min,
  parseISO,
} from 'date-fns';

import apiClient from '@/constants/http';
import type { Service } from '@/types/booking';
import { retryOnServerError, throwOnHttpError } from '@/utils/apiErrors';
import { transformServiceIds } from '@/utils/bookingCartUtils';

import type {
  GroupAppointmentForSchedule,
  GroupScheduleQueryParams,
  GroupScheduleResponse,
} from './types';

const API_DATE_FORMAT = 'yyyy-MM-dd';
// Group appointments use 2-week intervals (like WOW mobile)
// This is different from single appointments which use 4-week intervals
const MAX_WEEKS_IN_REQUEST = 2;

interface FetchInterval {
  start: string;
  end: string;
}

/**
 * Splits a date range into intervals of max N weeks each.
 * This matches WOW mobile's approach for better API performance.
 */
function getScheduleFetchIntervals(
  startDateISO: string,
  endDateISO: string,
  maxAllowedWeeks: number,
): FetchInterval[] {
  const weekStartsOn = 0; // Sunday - sync requests across all locales
  const weeksToAdd = Math.max(maxAllowedWeeks - 1, 0);
  const reservationInterval = {
    start: parseISO(startDateISO),
    end: parseISO(endDateISO),
  };

  return eachWeekOfInterval(reservationInterval, { weekStartsOn })
    .filter((_, idx) => idx % maxAllowedWeeks === 0) // every N-th week
    .map((week, idx, arr) => {
      const firstWeek = week;
      const intervalStart =
        idx === 0 ? max([firstWeek, reservationInterval.start]) : firstWeek;

      const lastWeek = endOfWeek(addWeeks(week, weeksToAdd), { weekStartsOn });
      const intervalEnd =
        idx === arr.length - 1
          ? min([lastWeek, reservationInterval.end])
          : lastWeek;

      if (isAfter(intervalStart, intervalEnd)) {
        throw new Error('Negative interval'); // This should never happen
      }

      return {
        start: format(intervalStart, API_DATE_FORMAT),
        end: format(intervalEnd, API_DATE_FORMAT),
      };
    });
}

async function fetchGroupSchedule({
  shopId,
  startDate,
  endDate,
  appointments,
}: GroupScheduleQueryParams): Promise<GroupScheduleResponse> {
  // Split the date range into 2-week chunks (like WOW mobile)
  const intervals = getScheduleFetchIntervals(
    startDate,
    endDate,
    MAX_WEEKS_IN_REQUEST,
  );

  // Make parallel requests for each interval
  const requests = intervals.map(({ start, end }) =>
    // Resolve every status but 401 so the status survives the api client's
    // interceptor (it rejects with the body alone) and `retry` can act on it.
    apiClient
      .post<GroupScheduleResponse>(
        `/v2/shop/${shopId}/schedule-time-range/${start}/${end}`,
        { appointments },
        { validateStatus: (status) => status !== 401 },
      )
      .then(throwOnHttpError),
  );

  // Execute all requests in parallel and combine results
  const responses = await Promise.all(requests);
  return responses.reduce<GroupScheduleResponse>(
    (acc, response) => acc.concat(response.data),
    [],
  );
}

/**
 * Query options for fetching group appointment schedule.
 * This uses the v2 POST endpoint that accepts all appointments with their current
 * timeslots, allowing the backend to mark already-selected slots as unavailable.
 *
 * @param params - Query parameters including shop, date range, and appointments
 */
export const getGroupScheduleQuery = (params: GroupScheduleQueryParams) => {
  const { shopId, startDate, endDate, appointments } = params;

  // Create a stable key that includes appointment timeslots for cache invalidation
  // When any appointment's timeslot changes, the query will refetch.
  // IMPORTANT: We use index instead of appointment ID because the server returns
  // new IDs on every cart upsert. Using IDs would cause unnecessary refetches
  // when IDs change but the actual appointment data (barber, services, timeslot) stays the same.
  const appointmentTimeslots = appointments
    .map(
      (a, index) =>
        `${index}:${a.barberId ?? 'any'}:${a.serviceIds.join('+')}:${a.timeslot ?? 'null'}`,
    )
    .join(',');

  return queryOptions({
    queryKey: [
      'group-schedule',
      shopId,
      startDate,
      endDate,
      appointmentTimeslots,
    ],
    queryFn: () => fetchGroupSchedule(params),
    enabled: appointments.length > 0,
    // Short cache so returning to Choose Time does not refetch all availability.
    // A taken slot is still caught: a rejected upsert refetches the schedule.
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: retryOnServerError,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    // Keep showing previous data while fetching new data with updated timeslots
    // This prevents the loading skeleton from appearing during time slot selection
    placeholderData: keepPreviousData,
  });
};

/**
 * Helper to build appointments array for the group schedule query.
 *
 * IMPORTANT: Cart items may contain parent IDs due to optimistic updates in
 * useOptimisticCartUpdater. The schedule API expects actual service IDs
 * (same as the cart update endpoint), so we need to transform parent IDs
 * back to service IDs using the same logic as bookingCartUtils.
 *
 * @param cartItems - Cart items which may have parent IDs in services
 * @param services - Optional services list for transforming parent IDs to service IDs
 */
export const buildAppointmentsForSchedule = (
  cartItems: Array<{
    id: string;
    barberId?: string | null;
    barber?: { id: string } | null;
    bookedWithAnyBarber?: boolean;
    dateTime?: string | null;
    services: Array<{ id: string }>;
  }>,
  services?: Service[],
): GroupAppointmentForSchedule[] => {
  return cartItems
    .filter((item) => item.services.length > 0) // Only include items with services
    .map((item) => ({
      id: item.id,
      barberId:
        item.bookedWithAnyBarber || !item.barber
          ? null
          : (item.barber?.id ?? item.barberId ?? null),
      // `|| null` (not `?? null`): the optimistic cart write clears a time by
      // setting `dateTime: ''` while the server returns `null`. Both mean "no
      // time selected", so they must normalize to the same value — the query
      // key embeds this, and two spellings made one day switch refetch twice,
      // flashing the loading skeleton twice (SQR-14091).
      timeslot: item.dateTime || null,
      // Transform parent IDs to service IDs to match what the cart update sends
      serviceIds: transformServiceIds(
        item.services.map((s) => s.id),
        services,
      ),
    }));
};
